<section
  class="shipment-details-print"
  data-testid="shipment-details-print"
  aria-labelledby="shipment-details-print-title"
>
  @if (confirmation(); as completed) {
    <img
      src="assets/Purolator-logo-horizontal.svg"
      alt="Purolator"
      class="shipment-details-print__logo"
      data-testid="shipment-details-print-logo"
    />

    <header class="shipment-details-print__header">
      <puro-typography
        id="shipment-details-print-title"
        component="h1"
        variant="h5"
        color="secondary"
        className="shipment-details-print-heading"
        class="shipment-details-print__title"
      >
        {{ 'createShipment.shipmentDetailsPrint.title' | translate }}
      </puro-typography>
      <puro-button
        variant="primary"
        size="small"
        [icon]="true"
        className="shipment-details-print-button"
        class="shipment-details-print__print-button"
        data-testid="shipment-details-print-button"
        (buttonClick)="triggerPrint()"
      >
        <img
          icon
          class="shipment-details-print__print-icon"
          src="assets/icons/print.svg"
          alt=""
          aria-hidden="true"
        />
        {{ 'createShipment.shipmentDetailsPrint.printDownload' | translate }}
      </puro-button>
    </header>

    <p class="shipment-details-print__reference">
      <span>
        {{ 'createShipment.getLabels.trackingPinLabel' | translate }}
        <strong>{{ completed.trackingPin }}</strong>
      </span>
      <span>
        {{ 'createShipment.getLabels.shipmentCreationDateLabel' | translate }}
        <strong>{{ formattedCreationDate() }}</strong>
      </span>
    </p>

    <div class="shipment-details-print__columns">
      @for (card of printAddressCards(); track card.testId) {
        <section class="shipment-details-print__card" [attr.data-testid]="card.testId">
          <puro-typography
            component="h2"
            variant="xsmall-semibold"
            class="shipment-details-print__card-title"
          >
            {{ card.titleKey | translate }}
          </puro-typography>
          @if (card.address; as address) {
            @let addressLines = addressBlockLines(address);
            <div class="shipment-details-print__address-group">
              @if (addressLines.length > 0) {
                <p class="shipment-details-print__address-block">
                  @for (line of addressLines; track $index; let last = $last) {
                    {{ line }}
                    @if (!last) {
                      <br />
                    }
                  }
                </p>
              }
              @if (address.contactName || address.email || address.phone) {
                <div class="shipment-details-print__contact">
                  @if (address.contactName) {
                    <p>
                      {{ 'createShipment.reviewAndPay.contactNameLabel' | translate }}
                      {{ address.contactName }}
                    </p>
                  }
                  @if (address.email) {
                    <p>
                      {{ 'createShipment.reviewAndPay.emailLabel' | translate }} {{ address.email }}
                    </p>
                  }
                  @if (address.phone) {
                    <p>
                      {{ 'createShipment.reviewAndPay.telLabel' | translate }} {{ address.phone }}
                    </p>
                  }
                </div>
              }
            </div>
          }
        </section>
      }
    </div>

    <div class="shipment-details-print__columns">
      @if (summaryViews().packagingView; as packaging) {
        <section
          class="shipment-details-print__card shipment-details-print__card--packaging"
          data-testid="shipment-details-print-packaging"
        >
          <puro-typography
            component="h2"
            variant="xsmall-semibold"
            class="shipment-details-print__card-title"
          >
            {{ 'createShipment.reviewAndPay.cards.packaging' | translate }}
          </puro-typography>
          <p class="shipment-details-print__row">
            <span>{{ 'createShipment.reviewAndPay.packaging.type' | translate }}</span>
            <span>{{ packaging.typeI18nKey | translate }}</span>
          </p>
          <p class="shipment-details-print__row">
            <span>{{ 'createShipment.reviewAndPay.packaging.totalPieces' | translate }}</span>
            <span>{{ packaging.totalPieces }}</span>
          </p>
          <p class="shipment-details-print__row">
            <span>{{ 'createShipment.reviewAndPay.packaging.totalWeight' | translate }}</span>
            <span>
              {{ packaging.weightDisplay }}
              {{ 'createShipment.reviewAndPay.packaging.' + packaging.weightUnit | translate }}
            </span>
          </p>
          @if (packaging.specialHandlingRows.length === 0) {
            <p class="shipment-details-print__row">
              <span>{{ 'createShipment.reviewAndPay.packaging.specialHandling' | translate }}</span>
              <span>{{ 'createShipment.reviewAndPay.packaging.no' | translate }}</span>
            </p>
          } @else {
            <p class="shipment-details-print__row shipment-details-print__row--heading">
              <span>{{ 'createShipment.reviewAndPay.packaging.specialHandling' | translate }}</span>
            </p>
            @for (row of packaging.specialHandlingRows; track row.typeKey) {
              <p class="shipment-details-print__row shipment-details-print__row--nested">
                <span>{{
                  'createShipment.reviewAndPay.packaging.types.' + row.typeKey | translate
                }}</span>
                <span>
                  @if (row.pieceCount === 1) {
                    {{ 'createShipment.reviewAndPay.packaging.pieceCountOne' | translate }}
                  } @else {
                    {{
                      'createShipment.reviewAndPay.packaging.pieceCountOther'
                        | translate: { count: '' + row.pieceCount }
                    }}
                  }
                </span>
              </p>
            }
          }
        </section>
      }

      @if (summaryViews().inductionView; as induction) {
        <section
          class="shipment-details-print__card shipment-details-print__card--pickup"
          data-testid="shipment-details-print-pickup"
        >
          <puro-typography
            component="h2"
            variant="xsmall-semibold"
            class="shipment-details-print__card-title"
          >
            {{ induction.titleI18nKey | translate }}
          </puro-typography>
          @if (induction.pickupConfirmationId) {
            <p class="shipment-details-print__row">
              <span>{{
                'createShipment.reviewAndPay.induction.pickupConfirmationId' | translate
              }}</span>
              <span>{{ induction.pickupConfirmationId }}</span>
            </p>
          }
          @if (induction.pickupDateLabel) {
            <p class="shipment-details-print__row">
              <span>{{ 'createShipment.reviewAndPay.induction.pickupDate' | translate }}</span>
              <span>{{ induction.pickupDateLabel }}</span>
            </p>
          }
          @if (induction.pickupTimeLabel) {
            <p class="shipment-details-print__row">
              <span>{{ 'createShipment.reviewAndPay.induction.pickupTime' | translate }}</span>
              <span>{{ induction.pickupTimeLabel }}</span>
            </p>
          }
          @if (induction.locationLabel || induction.locationI18nKey) {
            <p class="shipment-details-print__row">
              <span>{{ 'createShipment.reviewAndPay.induction.location' | translate }}</span>
              <span>{{ induction.locationLabel ?? (induction.locationI18nKey! | translate) }}</span>
            </p>
          }
          @if (inductionNote(); as note) {
            <p data-testid="shipment-details-print-induction-note">{{ note }}</p>
          }
        </section>
      }
    </div>

    <puro-typography
      component="h2"
      variant="h5"
      color="secondary"
      className="shipment-details-print-heading"
      class="shipment-details-print__section-title"
    >
      {{ 'createShipment.getLabels.estimatedCostsTitle' | translate }}
    </puro-typography>

    <div class="shipment-details-print__columns">
      @if (summaryViews().breakdown; as breakdown) {
        <section class="shipment-details-print__costs" data-testid="shipment-details-print-costs">
          <p class="shipment-details-print__row">
            <span>{{
              'createShipment.serviceStep.deliveryService.breakdown.baseCost' | translate
            }}</span>
            <span>{{ formatCurrencyAmount(breakdown.baseCost) }}</span>
          </p>
          @if (breakdown.additionalSurcharges.length > 0) {
            <div class="shipment-details-print__surcharges">
              <p class="shipment-details-print__surcharges-title">
                {{
                  'createShipment.serviceStep.deliveryService.breakdown.additionalSurcharges'
                    | translate
                }}
              </p>
              @for (line of breakdown.additionalSurcharges; track line.code) {
                <p class="shipment-details-print__row">
                  <span>{{ surchargeLabelKey(line.labelCode) | translate }}</span>
                  <span>
                    @if (line.displayMode === 'included') {
                      {{
                        'createShipment.serviceStep.deliveryService.breakdown.included' | translate
                      }}
                    } @else {
                      {{ formatCurrencyAmount(line.listAmount) }}
                    }
                  </span>
                </p>
              }
            </div>
          }
          <div class="shipment-details-print__taxes">
            <p class="shipment-details-print__row">
              <span>{{
                'createShipment.serviceStep.deliveryService.breakdown.fuelSurcharge' | translate
              }}</span>
              <span>{{ formatCurrencyAmount(breakdown.fuelSurcharge) }}</span>
            </p>
            <p class="shipment-details-print__row">
              <span>{{
                'createShipment.serviceStep.deliveryService.breakdown.gstHst' | translate
              }}</span>
              <span>{{ formatCurrencyAmount(breakdown.gstHst) }}</span>
            </p>
          </div>
          <p class="shipment-details-print__row shipment-details-print__row--total">
            <span>{{
              'createShipment.serviceStep.deliveryService.breakdown.total' | translate
            }}</span>
            <span>{{ formatCurrencyAmount(breakdown.total) }}</span>
          </p>
        </section>
      }

      <div class="shipment-details-print__card-stack">
        @if (summaryViews().selectedService; as service) {
          <section
            class="shipment-details-print__card"
            data-testid="shipment-details-print-service"
          >
            <puro-typography
              component="h2"
              variant="xsmall-semibold"
              class="shipment-details-print__card-title"
            >
              {{ 'createShipment.shipmentDetailsPrint.service' | translate }}
            </puro-typography>
            <div>
              <p class="shipment-details-print__service-name">
                {{ formatPrintServiceName(service.displayName) }}
              </p>
              <p>
                {{ 'createShipment.serviceStep.deliveryService.arrivingOn' | translate }}
                {{ formattedArrivalDate() }}
              </p>
            </div>
          </section>
        }

        @if (paymentDisplay(); as payment) {
          <div class="shipment-details-print__payment-stack">
            <section
              class="shipment-details-print__card shipment-details-print__card--payment"
              data-testid="shipment-details-print-payment"
            >
              <puro-typography
                component="h2"
                variant="xsmall-semibold"
                class="shipment-details-print__card-title"
              >
                {{ 'createShipment.getLabels.paymentMethod' | translate }}
              </puro-typography>
              <div class="shipment-details-print__payment-method-row">
                @if (resolvePaymentCardBrandIconSrc(payment.cardBrand); as brandIconSrc) {
                  <img
                    class="shipment-details-print__payment-brand-logo"
                    data-testid="shipment-details-print-payment-brand"
                    [src]="brandIconSrc"
                    [alt]="formatPaymentCardBrand(payment.cardBrand)"
                    width="76"
                    height="48"
                  />
                }
                <p class="shipment-details-print__payment-details">
                  {{ payment.maskedPan }}<br />
                  {{
                    'createShipment.getLabels.paymentExpiry' | translate: { expiry: payment.expiry }
                  }}
                </p>
              </div>
            </section>

            <section
              class="shipment-details-print__card"
              data-testid="shipment-details-print-billing"
            >
              <puro-typography
                component="h2"
                variant="xsmall-semibold"
                class="shipment-details-print__card-title"
              >
                {{ 'createShipment.getLabels.billingAddress' | translate }}
              </puro-typography>
              <p>
                {{ payment.billingName }}<br />
                {{ payment.billingPostalCode }}
              </p>
            </section>
          </div>
        }
      </div>
    </div>
  }
</section>
